import React from 'react'
import { Table } from 'antd'
import '../../css/measurement.scss'
import _ from 'lodash'
import { formatDateTimeET } from '../../util/datetime'

const PatientMeasurementTable = (props) => {
  // TracPatch is the source of truth for Extension/Flexion/Steps when present
  // — overrides the questionnaire-derived Extension/Flexion (WEIGHTROM) and
  // any device-derived steps (e.g. Tenovi Watch). When TracPatch is absent the
  // existing fallbacks apply.
  //
  // `latestRomTest` means a completed ROM TEST, nothing else. Since 2026-10-30
  // the server matches is_rom_test:true (business/patient.js fetchTracPatchSummary)
  // instead of falling back to any ROM record, so a patient wearing a device
  // but yet to run a Progress Test arrives here as null and correctly drops to
  // the questionnaire values. Before that fix such a patient showed their
  // knee's angle at an arbitrary minute as though it were a range of motion.
  const hasTracpatchRom = !!(
    props.latestRomTest &&
    (props.latestRomTest.flexion != null || props.latestRomTest.extension != null)
  )
  const hasTracpatchSteps = !!(
    props.lastDaySteps && props.lastDaySteps.total != null
  )

  var activedevices = props.data?.assigneddevices.filter(
    (dev) => !dev.isDeleted
  )
  var lastscore = []
  let devArray = []
  if (props.data?.lastscore && lastscore) {
    lastscore = activedevices?.map((dev) =>
      _.find(props.data?.lastscore, { deviceid: dev.deviceid })
    )
    lastscore.forEach((dev, index) => {
      if (
        dev &&
        Array.isArray(dev.value) &&
        dev.value?.length > 0 &&
        dev.device_unit?.length > 0
      )
        dev.value.forEach((v, i) => {
          if (v != 0 && v != null) {
            const metric = dev.device_metric[i]?.replaceAll('_', ' ')
            // If TracPatch is supplying steps, suppress device-derived step
            // metrics (e.g. Tenovi Watch) so we show one canonical "Steps" row.
            if (hasTracpatchSteps && /step/i.test(metric || '')) return
            devArray.push({
              ...dev,
              value: `${v} ${dev.device_unit[i] == 'percent' ? '%' : dev.device_unit[i]}`,
              measurement: metric,
            })
          }
        })
      // else devArray.push({...dev,value: dev.value,measurement:dev?.display_name});
    })
  }
  if (
    props.feed?.lastscores?.kneescore !== undefined &&
    props.feed?.lastscores?.kneescore !== null
  )
    devArray.push({
      measurement: 'Knee score',
      value: props.feed?.lastscores?.kneescore,
      date: props.feed?.lastscores?.kneescoredate,
    })
  if (
    props.feed?.lastscores?.hipscore !== undefined &&
    props.feed?.lastscores?.hipscore !== null
  )
    devArray.push({
      measurement: 'Hip score',
      value: props.feed?.lastscores?.hipscore,
      date: props.feed?.lastscores?.hipscoredate,
    })
  if (
    props.feed?.lastscores?.koosjrscore !== undefined &&
    props.feed?.lastscores?.koosjrscore !== null
  )
    devArray.push({
      measurement: 'KOOS JR',
      value: props.feed?.lastscores?.koosjrscore,
      date: props.feed?.lastscores?.koosjrscoredate,
    })
  if (
    props.feed?.lastscores?.hoosjrscore !== undefined &&
    props.feed?.lastscores?.hoosjrscore !== null
  )
    devArray.push({
      measurement: 'HOOS JR',
      value: props.feed?.lastscores?.hoosjrscore,
      date: props.feed?.lastscores?.hoosjrscoredate,
    })
  // Extension / Flexion: prefer the latest active TracPatch ROM Test when
  // present. Otherwise fall back to the WEIGHTROM questionnaire fields.
  if (hasTracpatchRom) {
    const ext = props.latestRomTest.extension
    const flex = props.latestRomTest.flexion
    const fmt = (v) => (v == null ? null : Math.round(v) + '°')
    if (ext != null) devArray.push({
      measurement: 'Extension',
      value: fmt(ext),
      date: props.latestRomTest.date,
    })
    if (flex != null) devArray.push({
      measurement: 'Flexion',
      value: fmt(flex),
      date: props.latestRomTest.date,
    })
  } else {
    if (props.feed?.lastscores?.extension)
      devArray.push({
        measurement: 'Extension',
        value: props.feed?.lastscores?.extension,
        date: props.feed?.lastscores?.extensiondate,
      })
    if (props.feed?.lastscores?.flexion)
      devArray.push({
        measurement: 'Flexion',
        value: props.feed?.lastscores?.flexion,
        date: props.feed?.lastscores?.flexiondate,
      })
  }
  if (props.feed?.lastscores?.painlevel)
    devArray.push({
      measurement: 'Pain level',
      value: props.feed?.lastscores?.painlevel,
      date: props.feed?.lastscores?.painleveldate,
    })

  // Pain Score (new 0–10 NRS questionnaire) — pulled out of the per-form list
  // by shortname. Distinct from the legacy "Pain level" above which comes from
  // the older Pain Management form.
  //
  // The form lives in lastscores.forms[] from the moment it's added to the
  // patient's schedule (with default score "0" and no date), so checking
  // score alone would render a misleading "0" row for unsubmitted forms.
  // Skip unless the form has a real submission date.
  const painScoreForm = (props.feed?.lastscores?.forms || []).find(
    (f) => f && f.shortname === 'Pain Score'
  )
  if (
    painScoreForm &&
    painScoreForm.date &&
    painScoreForm.score != null &&
    painScoreForm.score !== ''
  ) {
    devArray.push({
      measurement: 'Pain Score',
      value: painScoreForm.score,
      date: painScoreForm.date,
    })
  }

  // Temperature — latest femur/tibia reading from TracPatch. Stored in °C;
  // shown in °F to match the temperature chart's default. One combined row.
  const cToF = (c) => (c == null ? null : Math.round(((c * 9) / 5 + 32) * 10) / 10)
  if (
    props.lastDayTemp &&
    (props.lastDayTemp.femur != null || props.lastDayTemp.tibia != null)
  ) {
    const femurF = cToF(props.lastDayTemp.femur)
    const tibiaF = cToF(props.lastDayTemp.tibia)
    const parts = []
    if (femurF != null) parts.push('Femur ' + femurF + '°F')
    if (tibiaF != null) parts.push('Tibia ' + tibiaF + '°F')
    devArray.push({
      measurement: 'Temperature',
      value: parts.join(', '),
      date: props.lastDayTemp.date,
    })
  }

  // Steps — total for the most recent past calendar day from TracPatch. When
  // present, this replaces any device-derived step metric (filtered above).
  if (hasTracpatchSteps) {
    devArray.push({
      measurement: 'Steps',
      value: Number(props.lastDaySteps.total).toLocaleString(),
      date: props.lastDaySteps.date,
    })
  }
  let finArr = []
  devArray = _.sortBy(
    devArray,
    (obj) => new Date(obj.date ? new Date(obj.date.toString()) : 0)
  ).reverse()
  devArray.forEach((i) => {
    if (
      (i.value >= 0 || typeof i.value != 'number') &&
      finArr.find((val) => val.measurement === i.measurement) == undefined
    )
      finArr.push({
        // Keep the raw timestamp; the column formats it as ET date+time.
        ...i,
        date: i.date == null ? '' : i.date,
      })
  })
  const columns = [
    {
      title: 'Measurement',
      dataIndex: 'measurement',
      key: 'measurement',
      align: 'left',
      render: (measurement) => {
        return _.upperFirst(measurement)
      },
    },
    {
      title: 'Last Recorded',
      dataIndex: 'date',
      key: 'date',
      // Keep the date and time on a single line (the column is wide enough now).
      render: (date) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatDateTimeET(date)}</span>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'left',
    },
  ]

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Table
        locale={{ emptyText: 'Data not available' }}
        columns={columns}
        pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
        dataSource={finArr}
        className={`mesurement-table ${props.isMessage ? 'mesurement-table-message' : ''}`}
        onClick={(e) => e.stopPropagation()}
        justify={'center'}
      ></Table>
    </div>
  )
}

export default PatientMeasurementTable
